// Motion-comic fallback (rebuild.md §7.8): when an excerpt can't play (offline and not downloaded, or an
// error) the chapter's graphic-novel pages play panel by panel — pan/zoom to each panel box, tap to advance.
// The pages carry the lettering; the panel text is also exposed to screen readers.
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { reducedMotion } from '@app/state.ts';
import { EXCERPTS } from '@content/excerpts.ts';
import { mediaUrl } from './manifest.ts';
import './media.css';

export interface ComicPage {
  page: number;
  chapter: [string, string] | null;
  file: string;
  width: number;
  height: number;
  panels: { box: [number, number, number, number]; text: { kind: string; text: string }[] }[];
}

export interface ComicScript {
  width: number;
  always: number[];
  stages: Record<string, [number, number]>;
  excerpts: Record<string, { page: number; panels?: number[] }[]>;
  pages: ComicPage[];
}

export const comicScript = signal<ComicScript | null>(null);

export async function loadComic(): Promise<ComicScript | null> {
  if (comicScript.value) return comicScript.value;
  try {
    const r = await fetch('/media/comic/script.json');
    if (!r.ok) return null;
    comicScript.value = (await r.json()) as ComicScript;
    return comicScript.value;
  } catch {
    return null;
  }
}

export interface Shot {
  page: ComicPage;
  /** Panel box in page pixels, or the whole page. */
  box: [number, number, number, number];
  text: string;
}

/** The panel sequence shown for an excerpt. */
export function shotsFor(script: ComicScript, excerptId: string): Shot[] {
  const ex = EXCERPTS.find((e) => e.id === excerptId);
  const list =
    script.excerpts[excerptId] ??
    (() => {
      const r = ex ? script.stages[ex.stage] : undefined;
      if (!r) return [];
      return Array.from({ length: r[1] - r[0] + 1 }, (_, i): { page: number; panels?: number[] } => ({ page: r[0] + i }));
    })();
  const shots: Shot[] = [];
  for (const item of list) {
    const page = script.pages.find((p) => p.page === item.page);
    if (!page) continue;
    const idx = item.panels ?? page.panels.map((_, i) => i);
    if (!page.panels.length) {
      shots.push({ page, box: [0, 0, page.width, page.height], text: '' });
      continue;
    }
    for (const i of idx) {
      const pn = page.panels[i];
      if (pn) shots.push({ page, box: pn.box, text: pn.text.map((t) => t.text).join(' ') });
    }
  }
  return shots;
}

export function MotionComic(p: { excerptId: string; notice?: string; onEnded: () => void; onSkip: () => void }) {
  const [script, setScript] = useState<ComicScript | null>(comicScript.value);
  const [missing, setMissing] = useState(false);
  const [i, setI] = useState(0);
  const stage = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 1280, h: 720 });

  useEffect(() => {
    void loadComic().then((s) => (s ? setScript(s) : setMissing(true)));
    const measure = () => stage.current && setSize({ w: stage.current.clientWidth, h: stage.current.clientHeight });
    measure();
    addEventListener('resize', measure);
    return () => removeEventListener('resize', measure);
  }, []);

  const shots = script ? shotsFor(script, p.excerptId) : [];
  const next = () => (i + 1 >= shots.length ? p.onEnded() : setI(i + 1));
  const prev = () => setI(Math.max(0, i - 1));

  if (missing || (script && !shots.length)) {
    return (
      <main id="main" class="motion-comic comic-missing">
        <p>This chapter’s film and pages aren’t available offline. Download the chapter while you’re online to watch it here.</p>
        <button type="button" class="btn btn-primary" onClick={p.onEnded}>
          Continue
        </button>
      </main>
    );
  }
  const shot = shots[i];
  let transform = 'none';
  let pageStyle: Record<string, string> = {};
  if (shot) {
    const [x0, y0, x1, y1] = shot.box;
    const bw = x1 - x0;
    const bh = y1 - y0;
    const scale = Math.min(size.w / bw, size.h / bh) * 0.96;
    const tx = size.w / 2 - (x0 + bw / 2) * scale;
    const ty = size.h / 2 - (y0 + bh / 2) * scale;
    transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    pageStyle = { width: `${shot.page.width}px`, height: `${shot.page.height}px`, transform, transition: reducedMotion() ? 'none' : 'transform 700ms cubic-bezier(.2,.7,.2,1)' };
  }
  return (
    <main
      id="main"
      class="motion-comic"
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          next();
        }
        if (e.key === 'ArrowLeft') prev();
      }}
    >
      {p.notice && <p class="comic-notice">{p.notice}</p>}
      <div class="comic-stage" ref={stage} onClick={next} role="presentation">
        {shot && <img class="comic-page" src={mediaUrl(shot.page.file) ?? ''} alt="" style={pageStyle} draggable={false} />}
      </div>
      <p class="visually-hidden" aria-live="polite">
        {shot?.text || `Panel ${i + 1}`}
      </p>
      <div class="comic-controls">
        <button type="button" class="cs-btn" onClick={prev} disabled={i === 0} aria-label="Previous panel">
          ◀
        </button>
        <span class="cs-time">
          {Math.min(i + 1, shots.length)} / {shots.length}
        </span>
        <button type="button" class="cs-btn" onClick={next} aria-label="Next panel" ref={(el) => el?.focus()}>
          ▶
        </button>
        <button type="button" class="cs-btn cs-text" onClick={p.onSkip}>
          Skip
        </button>
      </div>
    </main>
  );
}
